import React, { useState, useEffect, useRef } from 'react';
import CategoryCard from './CategoryCard';
import CategoryCardSkeleton from './CategoryCardSkeleton';
import SubcategoryService, { Subcategory } from '../../services/subcategory.service';

const CategorySlider: React.FC = () => {
  const sliderRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [categories, setCategories] = useState<Subcategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Cargar las categorías con sus servicios incluidos
  useEffect(() => {
    const fetchCategoriesWithServices = async () => {
      try {
        setLoading(true);
        // Obtener todas las subcategorías con sus servicios incluidos
        const subcategoriesWithServices = await SubcategoryService.getAllSubcategories();
        //console.log('Subcategorías con servicios:', subcategoriesWithServices);
        setCategories(subcategoriesWithServices);
        setLoading(false);

      } catch (err) {
        console.error('Error al cargar categorías:', err);
        setError('No se pudieron cargar las categorías. Por favor, intenta de nuevo más tarde.');
        setLoading(false);
      }
    };

    fetchCategoriesWithServices();
  }, []);

  //console.log('Categorias:', categories);

  // Función para desplazar el slider
  const scroll = (direction: 'left' | 'right') => {
    if (sliderRef.current) {
      const { scrollLeft, clientWidth } = sliderRef.current;
      const scrollTo = direction === 'left'
        ? scrollLeft - clientWidth * 0.8
        : scrollLeft + clientWidth * 0.8;

      sliderRef.current.scrollTo({
        left: scrollTo,
        behavior: 'smooth'
      });
    }
  };

  // Función para manejar el evento de scroll
  const handleScroll = () => {
    if (sliderRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = sliderRef.current;

      // Mostrar/ocultar flechas según la posición del scroll
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft + clientWidth < scrollWidth - 10);
    }
  };

  // Si está cargando, mostrar skeleton loader
  if (loading) {
    return (
      <div className="relative">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-6 text-center">
          Nuestras Categorías de <span className='text-xervix-primary font-semibold text-center'>Servicios</span>
        </h2>
        <p className='mb-4 text-center text-gray-600 dark:text-gray-300'>En XERVIX reunimos todas las categorías de servicios para el hogar en un solo lugar. Desde limpieza hasta remodelaciones, aquí encontrarás profesionales confiables listos para ayudarte. ¡Explora, elige y agenda en segundos!</p>

        {/* Contenedor del skeleton loader */}
        <div className="flex overflow-x-auto scrollbar-hide snap-x snap-mandatory py-4 px-2 md:px-4"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {/* Mostrar 5 skeletons mientras carga */}
          {[...Array(5)].map((_, index) => (
            <div key={index} className="snap-start">
              <CategoryCardSkeleton />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Si hay un error, mostrar mensaje de error
  if (error) {
    return (
      <div className="container mx-auto px-4 md:px-12 py-12">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-6">
          Categorías de Servicios
        </h2>
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <h2 className="text-3xl md:text-4xl font-bold text-gray-800 dark:text-white mb-6 text-center">
        Nuestras Categorías de <span className='text-xervix-primary font-bold text-center'>Servicios</span>
      </h2>
      <p className='mb-8 text-left text-gray-600 dark:text-gray-300'>En XERVIX reunimos todas las categorías de servicios para el hogar en un solo lugar. Desde limpieza hasta remodelaciones, aquí encontrarás profesionales confiables listos para ayudarte. ¡Explora, elige y agenda en segundos!</p>


      {/* Flecha izquierda */}
      {showLeftArrow && (
        <button
          onClick={() => scroll('left')}
          className="absolute left-2 md:left-4 top-1/2 transform -translate-y-1/2 z-20 bg-white/90 dark:bg-gray-800/90 rounded-full p-2 shadow-lg hover:bg-white dark:hover:bg-gray-700 transition-all"
          aria-label="Desplazar a la izquierda"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-600 dark:text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}

      {/* Flecha derecha */}
      {showRightArrow && (
        <button
          onClick={() => scroll('right')}
          className="absolute right-2 md:right-4 top-1/2 transform -translate-y-1/2 z-20 bg-white/90 dark:bg-gray-800/90 rounded-full p-2 shadow-lg hover:bg-white dark:hover:bg-gray-700 transition-all"
          aria-label="Desplazar a la derecha"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-600 dark:text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}

      {/* Contenedor del slider */}
      <div
        ref={sliderRef}
        className="flex overflow-x-auto scrollbar-hide snap-x snap-mandatory py-4 px-2 md:px-4"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        onScroll={handleScroll}
      >
        {categories.map((category) => {
          // Ahora los servicios vienen incluidos en cada categoría
          const services = category.serviceDefinitions || [];
          const featuredServices = services.map(service => service.name);

          return (
            <div key={category.id} className="snap-start">
              <CategoryCard
                id={category.id}
                name={category.name}
                imageUrl={category.imageUrl}
                featuredServices={featuredServices}
                serviceCount={services.length}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CategorySlider;
